import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Dumbbell, UtensilsCrossed, Users, TrendingDown, BookOpen, Quote } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/site/Reveal";
import EditorialMarquee from "@/components/site/Marquee";
import { IMAGES, EBOOK, TESTIMONIALS, TIKTOK, waLink } from "@/lib/data";
import { useLang } from "@/lib/i18n";

const Home = () => {
    const { scrollY } = useScroll();
    const heroY = useTransform(scrollY, [0, 800], [0, 180]);
    const heroScale = useTransform(scrollY, [0, 800], [1.05, 1.2]);
    const { tr } = useLang();

    const STATS = [
        { value: "250+", label: tr("Clients transformés", "Clients transformed") },
        { value: "5", label: tr("Années d'expérience", "Years of experience") },
        { value: "45", label: tr("Recettes dans l'Ebook", "Recipes in the Ebook") },
        { value: "97%", label: tr("Objectifs atteints", "Goals reached") },
    ];

    const CHAPTERS = [
        {
            num: "01",
            title: tr("La cuisine africaine est une force", "African cuisine is a strength"),
            text: tr(
                "Le fonio, le mil, l'igname, les feuilles de ndolé : des aliments puissants, denses et honnêtes. Ta culture culinaire n'est pas un obstacle à ta transformation — c'est ton avantage.",
                "Fonio, millet, yam, ndolé leaves: powerful, dense and honest foods. Your culinary culture is not an obstacle to your transformation — it's your advantage."
            ),
        },
        {
            num: "02",
            title: tr("La discipline sans la privation", "Discipline without deprivation"),
            text: tr(
                "On ne construit rien de durable sur la frustration. Chaque plan que je crée respecte tes plats préférés, recalibrés avec précision pour brûler le gras sans perdre le goût.",
                "Nothing lasting is built on frustration. Every plan I create respects your favourite dishes, precisely recalibrated to burn fat without losing the taste."
            ),
        },
        {
            num: "03",
            title: tr("Le corps forge l'esprit", "The body forges the mind"),
            text: tr(
                "La fonte enseigne la constance. La table enseigne la gratitude. Ensemble, elles forgent une discipline qui dépasse largement la salle de sport.",
                "Iron teaches consistency. The table teaches gratitude. Together, they forge a discipline that goes far beyond the gym."
            ),
        },
    ];

    const SERVICES = [
        { icon: Dumbbell, t: tr("Musculation", "Bodybuilding"), d: tr("Programmes sur mesure, salle ou maison, orientés force et esthétique.", "Custom programs, gym or home, focused on strength and aesthetics.") },
        { icon: UtensilsCrossed, t: tr("Nutrition africaine", "African nutrition"), d: tr("Plans de repas construits autour de ta culture, jamais contre elle.", "Meal plans built around your culture, never against it.") },
        { icon: TrendingDown, t: tr("Perte de gras", "Fat loss"), d: tr("Méthode progressive et durable, sans régime extrême ni frustration.", "A progressive and sustainable method, no extreme diets or frustration.") },
        { icon: Users, t: tr("Suivi humain", "Human support"), d: tr("Un coach disponible, exigeant et profondément investi dans ta réussite.", "An available, demanding coach who is deeply invested in your success.") },
    ];

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-screen flex items-end overflow-hidden">
                <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0 will-change-transform">
                    <img
                        src={IMAGES.heroCoach}
                        alt="Coach Roméo"
                        className="h-full w-full object-cover object-[center_15%]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/65 to-obsidian/35" />
                    <div className="absolute inset-0 bg-black/35" />
                </motion.div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pb-20 md:pb-28 pt-40">
                    <MaskedLine delay={0.15}>
                        <Overline>{tr("Musculation · Nutrition · Fierté africaine", "Bodybuilding · Nutrition · African pride")}</Overline>
                    </MaskedLine>
                    <h1 className="mt-6 font-heading font-black uppercase tracking-tighter leading-[0.92] text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
                        <MaskedLine delay={0.3}>{tr("Forge ton corps.", "Forge your body.")}</MaskedLine>
                        <MaskedLine delay={0.45}>
                            <span className="font-accent italic normal-case font-medium text-ochre">
                                {tr("Honore tes racines.", "Honour your roots.")}
                            </span>
                        </MaskedLine>
                    </h1>
                    <MaskedLine delay={0.65} className="mt-8 max-w-xl">
                        <p className="text-base md:text-lg text-cream/80 leading-relaxed font-light">
                            {tr(
                                "Coaching premium en musculation et nutrition, construit autour de la richesse de la cuisine africaine. Perds du gras, gagne de la force — sans jamais renier ce qui te nourrit.",
                                "Premium bodybuilding and nutrition coaching built on the richness of African cuisine. Lose fat, gain strength — without ever giving up the food that feeds you."
                            )}
                        </p>
                    </MaskedLine>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-10 flex flex-col sm:flex-row gap-4"
                    >
                        <Link
                            to="/contact"
                            data-testid="hero-cta-consultation"
                            className="inline-flex items-center justify-center gap-3 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-terracotta-hover"
                        >
                            {tr("Réserver ma consultation", "Book my consultation")} <ArrowRight className="h-4 w-4" />
                        </Link>
                        <Link
                            to="/ebook"
                            data-testid="hero-cta-ebook"
                            className="inline-flex items-center justify-center gap-3 rounded-full border border-white/25 px-8 py-4 text-sm font-bold uppercase tracking-wider text-cream transition-colors duration-300 hover:border-ochre hover:text-ochre"
                        >
                            <BookOpen className="h-4 w-4" /> {tr("Découvrir l'Ebook", "Discover the Ebook")}
                        </Link>
                    </motion.div>
                </div>

                <motion.div
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute bottom-8 right-8 md:right-12 z-10 text-cream/50"
                >
                    <ArrowDown className="h-6 w-6" />
                </motion.div>
            </section>

            <Link to="/ebook" data-testid="home-ebook-banner" className="block bg-ochre text-obsidian group">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between gap-4">
                    <p className="text-sm md:text-base font-bold uppercase tracking-wider">
                        {tr("L'Ebook est arrivé —", "The Ebook is here —")} {tr(EBOOK.title)} · {EBOOK.price}
                    </p>
                    <span className="shrink-0 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider">
                        {tr("Découvrir", "Discover")}
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                    </span>
                </div>
            </Link>

            <EditorialMarquee />

            {/* STATS */}
            <section className="py-16 md:py-24 border-b border-white/10" data-testid="home-stats">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4 gap-10">
                    {STATS.map((s, i) => (
                        <Reveal key={s.label} delay={i * 0.08}>
                            <p className="font-heading text-5xl md:text-6xl font-black tracking-tighter text-terracotta">
                                {s.value}
                            </p>
                            <p className="mt-2 text-sm uppercase tracking-[0.18em] text-sand">{s.label}</p>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* MANIFESTO */}
            <section className="py-24 md:py-32 lg:py-40" data-testid="home-manifesto">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <Overline>{tr("Le manifeste", "The manifesto")}</Overline>
                        <h2 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">
                            {tr("Trois convictions,", "Three beliefs,")}{" "}
                            <span className="font-accent italic font-medium text-ochre">{tr("une transformation.", "one transformation.")}</span>
                        </h2>
                    </Reveal>
                    <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
                        {CHAPTERS.map((c, i) => (
                            <Reveal
                                key={c.num}
                                delay={i * 0.12}
                                className={`md:col-span-4 ${i === 1 ? "md:mt-16" : ""} ${i === 2 ? "md:mt-32" : ""}`}
                            >
                                <div className="border border-white/10 bg-obsidian-surface p-8 md:p-10 h-full group hover:border-terracotta/50 transition-colors duration-500">
                                    <p className="font-accent italic text-6xl md:text-7xl text-terracotta/40 group-hover:text-terracotta transition-colors duration-500">
                                        {c.num}
                                    </p>
                                    <h3 className="mt-6 font-heading text-2xl md:text-3xl font-bold tracking-tight leading-tight">
                                        {c.title}
                                    </h3>
                                    <p className="mt-5 text-sand leading-relaxed font-light">{c.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* EBOOK SHOWCASE */}
            <section className="relative py-24 md:py-32 bg-obsidian-surface border-y border-white/10 overflow-hidden" data-testid="home-ebook-showcase">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <Reveal className="lg:col-span-5 order-2 lg:order-1">
                        <Overline>{tr("L'Ebook signature", "The signature Ebook")}</Overline>
                        <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
                            {tr(EBOOK.title)}
                        </h2>
                        <p className="mt-3 font-accent italic text-xl text-ochre">{tr(EBOOK.subtitle)}</p>
                        <ul className="mt-8 space-y-4">
                            {EBOOK.bullets.slice(0, 3).map((b) => (
                                <li key={b.fr} className="flex items-start gap-3 text-sand">
                                    <span className="mt-1.5 h-2 w-2 rounded-full bg-terracotta shrink-0" />
                                    {tr(b)}
                                </li>
                            ))}
                        </ul>
                        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6">
                            <Link
                                to="/ebook"
                                data-testid="home-ebook-cta"
                                className="inline-flex items-center justify-center gap-3 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-terracotta-hover"
                            >
                                {tr("Voir l'Ebook", "See the Ebook")} — {EBOOK.price} <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-7 order-1 lg:order-2">
                        <div className="relative">
                            <div className="absolute -inset-4 border border-ochre/25 translate-x-6 translate-y-6" />
                            <div className="relative overflow-hidden">
                                <img
                                    src={EBOOK.cover}
                                    alt={tr(EBOOK.title)}
                                    className="w-full aspect-[4/3] object-cover transition-transform duration-700 ease-out hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                                <div className="absolute bottom-6 left-6 right-6">
                                    <p className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight">
                                        {tr(EBOOK.title)}
                                    </p>
                                    <p className="text-ochre text-sm uppercase tracking-[0.2em] mt-1">Coach Roméo</p>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SERVICES */}
            <section className="py-24 md:py-32" data-testid="home-services">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <Overline>{tr("Ce que je t'apporte", "What I bring you")}</Overline>
                        <h2 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                            {tr("Un accompagnement", "Support that is")}{" "}
                            <span className="font-accent italic font-medium text-ochre">{tr("complet.", "complete.")}</span>
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {SERVICES.map((s, i) => (
                            <Reveal key={s.t} delay={i * 0.08}>
                                <div className="group border border-white/10 p-8 h-full hover:bg-obsidian-surface transition-colors duration-500">
                                    <s.icon className="h-8 w-8 text-terracotta transition-transform duration-500 group-hover:-translate-y-1" strokeWidth={1.6} />
                                    <h3 className="mt-6 font-heading text-xl font-bold uppercase tracking-tight">{s.t}</h3>
                                    <p className="mt-3 text-sand text-sm leading-relaxed font-light">{s.d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal className="mt-12">
                        <Link
                            to="/programmes"
                            data-testid="home-programs-link"
                            className="inline-flex items-center gap-3 text-ochre uppercase tracking-[0.2em] text-sm font-bold hover:gap-5 transition-all duration-300"
                        >
                            {tr("Voir les programmes et tarifs", "See programs and pricing")} <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* TESTIMONIAL PREVIEW */}
            <section className="py-24 md:py-32 bg-obsidian-surface border-y border-white/10" data-testid="home-testimonials-preview">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <Reveal>
                            <Overline>{tr("Témoignages clients", "Client testimonials")}</Overline>
                            <h2 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                                {tr("Des résultats", "Results that are")}{" "}
                                <span className="font-accent italic font-medium text-ochre">{tr("réels.", "real.")}</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <Link
                                to="/temoignages"
                                data-testid="home-testimonials-link"
                                className="inline-flex items-center gap-3 text-ochre uppercase tracking-[0.2em] text-sm font-bold hover:gap-5 transition-all duration-300"
                            >
                                {tr("Tous les témoignages", "All testimonials")} <ArrowRight className="h-4 w-4" />
                            </Link>
                        </Reveal>
                    </div>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {TESTIMONIALS.slice(0, 3).map((t, i) => (
                            <Reveal key={t.name} delay={i * 0.1}>
                                <figure className="border border-white/10 p-8 h-full flex flex-col">
                                    <Quote className="h-6 w-6 text-terracotta" />
                                    <blockquote className="mt-5 text-cream/85 leading-relaxed font-light flex-1">
                                        « {tr(t.quote)} »
                                    </blockquote>
                                    <figcaption className="mt-6 pt-6 border-t border-white/10">
                                        <p className="font-heading font-bold uppercase tracking-tight">{t.name}</p>
                                        <p className="text-sm text-ochre mt-1">{tr(t.result)}</p>
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* TIKTOK VIDEO */}
            <section className="py-24 md:py-32" data-testid="home-tiktok">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <Reveal className="lg:col-span-5">
                        <Overline>{tr("Sur TikTok", "On TikTok")} — {TIKTOK.handle}</Overline>
                        <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-bold tracking-tight">
                            {tr("Le coaching", "Coaching")}{" "}
                            <span className="font-accent italic font-medium text-ochre">{tr("en vidéo.", "on video.")}</span>
                        </h2>
                        <p className="mt-6 text-sand leading-relaxed font-light">
                            {tr(
                                "Entraînements, recettes africaines et motivation quotidienne : rejoins la communauté sur TikTok pour du contenu gratuit chaque semaine.",
                                "Workouts, African recipes and daily motivation: join the community on TikTok for free content every week."
                            )}
                        </p>
                        <a
                            href={TIKTOK.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="tiktok-follow-cta"
                            className="mt-8 inline-flex items-center gap-3 rounded-full border border-white/25 px-8 py-4 text-sm font-bold uppercase tracking-wider text-cream transition-colors duration-300 hover:border-ochre hover:text-ochre"
                        >
                            {tr("Suivre sur TikTok", "Follow on TikTok")} <ArrowRight className="h-4 w-4" />
                        </a>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-7 flex justify-center">
                        <div className="relative w-full max-w-xs md:max-w-sm">
                            <div className="absolute -inset-3 border border-terracotta/30 translate-x-4 translate-y-4 rounded-3xl" />
                            <video
                                src="/videos/coach-romeo-tiktok.mp4"
                                data-testid="tiktok-video"
                                className="relative w-full rounded-3xl border border-white/10 aspect-[9/16] object-cover bg-obsidian-surface"
                                autoPlay
                                muted
                                loop
                                playsInline
                                controls
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="relative py-28 md:py-40 overflow-hidden" data-testid="home-final-cta">
                <img src={IMAGES.programsBg} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-20" />
                <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/70 to-obsidian" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
                    <Reveal>
                        <h2 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.95]">
                            {tr("Ta transformation", "Your transformation")}<br />
                            <span className="font-accent italic normal-case font-medium text-ochre">
                                {tr("commence maintenant.", "starts now.")}
                            </span>
                        </h2>
                        <p className="mt-6 text-sand max-w-xl mx-auto leading-relaxed">
                            {tr(
                                "Réserve ta consultation de 30 minutes (25 $) en visioconférence Zoom, ou écris-moi directement sur WhatsApp.",
                                "Book your 30-minute consultation ($25) on Zoom, or message me directly on WhatsApp."
                            )}
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
                            <Link
                                to="/contact"
                                data-testid="home-final-cta-consultation"
                                className="inline-flex items-center justify-center gap-3 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-terracotta-hover"
                            >
                                {tr("Réserver sur le calendrier", "Book on the calendar")} <ArrowRight className="h-4 w-4" />
                            </Link>
                            <a
                                href={waLink(tr("Bonjour Coach Roméo, je veux démarrer ma transformation.", "Hello Coach Roméo, I want to start my transformation."))}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="home-final-cta-whatsapp"
                                className="inline-flex items-center justify-center gap-3 rounded-full border border-whatsapp/60 px-8 py-4 text-sm font-bold uppercase tracking-wider text-whatsapp transition-colors duration-300 hover:bg-whatsapp hover:text-white"
                            >
                                WhatsApp
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default Home;
